import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { SelectField } from "@/pages/FacilitiesPage";
import { toast } from "sonner";
import { Plus, Trash2, ChevronLeft, ChevronRight, CalendarDays, AlertOctagon, Building2, ListChecks, Repeat } from "lucide-react";

// -------- helpers --------
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAY_LABELS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const timeFmt = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" });
const dayFmt = new Intl.DateTimeFormat("en-US", { weekday: "long", month: "long", day: "numeric" });

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const startOfMonthGrid = (year, month) => {
  const first = new Date(year, month, 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  return start;
};

const endOfMonthGrid = (year, month) => {
  const first = new Date(year, month, 1);
  const start = startOfMonthGrid(year, month);
  const end = new Date(start);
  end.setDate(start.getDate() + 42); // 6 weeks
  return { start, end, first };
};

const toDatetimeLocal = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const KIND_STYLE = {
  meeting:          { dot: "#4A6B5D", chip: "bg-[#E9F1EC] text-[#2F5346] border-[#CFDFD5]", icon: CalendarDays, label: "Meeting" },
  facility_meeting: { dot: "#8AA79A", chip: "bg-[#EEF3F0] text-[#3E594D] border-[#D6DFDA]", icon: Building2,    label: "Facility visit" },
  issue:            { dot: "#C17D6E", chip: "bg-[#F7E9E5] text-[#8A3E30] border-[#E7C9C1]", icon: AlertOctagon,  label: "Issue due" },
  follow_up:        { dot: "#B08A55", chip: "bg-[#F4ECDD] text-[#6B4E1E] border-[#E1D0AA]", icon: ListChecks,   label: "Follow-up" },
};

// -------- main --------
export default function MeetingsPage() {
  const today = useMemo(() => new Date(), []);
  const [searchParams, setSearchParams] = useSearchParams();
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [selected, setSelected] = useState(() => new Date(today.getFullYear(), today.getMonth(), today.getDate()));
  const [events, setEvents] = useState([]);
  const [facs, setFacs] = useState([]);
  const [nps, setNps] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // meeting object being edited (or null for new)
  const [form, setForm] = useState({ title: "", when: "", attendees: "", facility_id: "", np_id: "", notes: "", recurrence: "none", recurrence_until: "" });

  const loadRefs = useCallback(async () => {
    try {
      const [{ data: f }, { data: n }] = await Promise.all([api.get("/facilities"), api.get("/nps")]);
      setFacs(f); setNps(n);
    } catch (e) { /* eslint-disable-next-line no-console */ console.warn("[calendar] refs failed:", e?.message); }
  }, []);

  const loadEvents = useCallback(async () => {
    // Load a window spanning current visible grid ± buffer.
    const { start, end } = endOfMonthGrid(cursor.getFullYear(), cursor.getMonth());
    try {
      const { data } = await api.get("/calendar", { params: { start: start.toISOString(), end: end.toISOString() } });
      setEvents(data || []);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[calendar] load failed:", e?.message);
      toast.error("Could not load calendar");
    }
  }, [cursor]);

  useEffect(() => { loadRefs(); }, [loadRefs]);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  // Auto-open scheduling sheet if URL has ?new=1 (from Overview quick-action)
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      const t = new Date();
      const seed = new Date(t.getFullYear(), t.getMonth(), t.getDate(), t.getHours() + 1, 0, 0, 0);
      setEditing(null);
      setForm({ title: "", when: toDatetimeLocal(seed), attendees: "", facility_id: "", np_id: "", notes: "" });
      setOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const facMap = Object.fromEntries(facs.map((f) => [f.id, f.name]));
  const npMap = Object.fromEntries(nps.map((n) => [n.id, n.name]));

  const { start } = endOfMonthGrid(cursor.getFullYear(), cursor.getMonth());
  const days = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });

  const eventsByDay = useMemo(() => {
    const map = new Map();
    events.forEach((ev) => {
      if (!ev.when) return;
      const d = new Date(ev.when);
      if (Number.isNaN(d.getTime())) return;
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(ev);
    });
    return map;
  }, [events]);

  const evsFor = (d) => eventsByDay.get(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`) || [];
  const selectedEvents = evsFor(selected).sort((a, b) => (a.when || "").localeCompare(b.when || ""));

  // Upcoming = next 14 days from today, excluding today (today is shown above)
  const upcoming = useMemo(() => {
    const start14 = new Date(); start14.setHours(0,0,0,0);
    const end14 = new Date(start14); end14.setDate(start14.getDate() + 14);
    return events
      .filter((e) => { const d = new Date(e.when); return !Number.isNaN(d.getTime()) && d >= start14 && d < end14; })
      .sort((a, b) => (a.when || "").localeCompare(b.when || ""));
  }, [events]);

  // Distinct recurring series across all events (uses meeting.series_id from /api/calendar)
  const recurringSeries = useMemo(() => {
    const map = new Map();
    const now = new Date();
    events.forEach((ev) => {
      if (ev.kind !== "meeting" || !ev.series_id) return;
      const d = new Date(ev.when);
      if (Number.isNaN(d.getTime())) return;
      const cur = map.get(ev.series_id) || { series_id: ev.series_id, title: ev.title, count: 0, next: null, first_id: ev.ref_id };
      cur.count += 1;
      if (d >= now && (!cur.next || d < new Date(cur.next.when))) cur.next = ev;
      map.set(ev.series_id, cur);
    });
    return Array.from(map.values()).sort((a, b) => (a.next?.when || "").localeCompare(b.next?.when || ""));
  }, [events]);

  const deleteSeries = async (ev) => {
    if (!ev?.ref_id) return;
    if (!window.confirm(`Delete this entire recurring series ("${ev.title}") including all future occurrences?`)) return;
    try {
      const { data } = await api.delete(`/meetings/${ev.ref_id}`, { params: { scope: "series" } });
      toast.success(`Removed ${data?.deleted || 0} occurrences`);
      loadEvents();
    } catch { toast.error("Delete failed"); }
  };

  const openNew = (dateObj) => {
    setEditing(null);
    const base = dateObj || selected;
    const seed = new Date(base);
    if (sameDay(seed, today)) seed.setHours(today.getHours() + 1, 0, 0, 0);
    else seed.setHours(9, 0, 0, 0);
    setForm({ title: "", when: toDatetimeLocal(seed), attendees: "", facility_id: "", np_id: "", notes: "", recurrence: "none", recurrence_until: "" });
    setOpen(true);
  };

  const openEdit = (ev) => {
    if (ev.kind !== "meeting") return; // only meetings are editable inline
    setEditing(ev);
    const d = new Date(ev.when);
    setForm({
      title: ev.title || "",
      when: toDatetimeLocal(d),
      attendees: ev.attendees || "",
      facility_id: ev.facility_id || "",
      np_id: ev.np_id || "",
      notes: ev.notes || "",
      recurrence: "none",
      recurrence_until: "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const iso = new Date(form.when).toISOString();
    const payload = {
      title: form.title,
      when: iso,
      attendees: form.attendees,
      facility_id: form.facility_id || null,
      np_id: form.np_id || null,
      notes: form.notes,
    };
    try {
      if (editing) {
        await api.put(`/meetings/${editing.ref_id}`, payload);
        toast.success("Meeting updated");
      } else {
        const post = { ...payload, recurrence: form.recurrence || "none", recurrence_until: form.recurrence_until || null };
        const { data } = await api.post("/meetings", post);
        if (data?.series_count > 1) toast.success(`Scheduled — ${data.series_count} occurrences`);
        else toast.success("Meeting scheduled");
      }
      setOpen(false);
      setEditing(null);
      loadEvents();
    } catch { toast.error("Failed to save"); }
  };

  const remove = async (ev) => {
    if (ev.kind !== "meeting") return;
    const isSeries = !!ev.series_id;
    let scope = "one";
    if (isSeries) {
      const choice = window.confirm(
        `"${ev.title}" is part of a recurring series.\n\nOK = delete THIS occurrence and all FUTURE ones.\nCancel = keep everything (nothing deleted).`
      );
      if (!choice) return;
      scope = "series";
    } else {
      if (!window.confirm(`Delete "${ev.title}"?`)) return;
    }
    try {
      const { data } = await api.delete(`/meetings/${ev.ref_id}`, { params: { scope } });
      toast.success(scope === "series" ? `Removed ${data?.deleted || 1} occurrences` : "Removed");
      loadEvents();
    } catch { toast.error("Delete failed"); }
  };

  const prevMonth = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const nextMonth = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const goToday = () => {
    const t = new Date();
    setCursor(new Date(t.getFullYear(), t.getMonth(), 1));
    setSelected(new Date(t.getFullYear(), t.getMonth(), t.getDate()));
  };

  const EventChip = ({ ev, compact = false }) => {
    const style = KIND_STYLE[ev.kind] || KIND_STYLE.meeting;
    const Icon = style.icon;
    if (compact) {
      return (
        <div
          className="truncate text-[11px] leading-tight rounded px-1.5 py-0.5 flex items-center gap-1"
          style={{ background: style.chip.match(/bg-\[(#[^\]]+)\]/)?.[1] || "#F0EBE1" }}
          title={ev.title}
        >
          <span className="inline-block h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ background: style.dot }} />
          <span className="truncate">{ev.title}</span>
        </div>
      );
    }
    const isEditable = ev.kind === "meeting";
    const activate = () => { if (isEditable) openEdit(ev); };
    return (
      <div
        role={isEditable ? "button" : undefined}
        tabIndex={isEditable ? 0 : undefined}
        onClick={isEditable ? activate : undefined}
        onKeyDown={isEditable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); activate(); } } : undefined}
        data-testid={`event-${ev.id}`}
        className={`relative border rounded-md p-3 transition-colors ${style.chip} ${isEditable ? "hover:brightness-95 cursor-pointer" : "cursor-default"}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Icon className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="text-[10px] uppercase tracking-[0.08em] font-semibold">{style.label}</span>
            </div>
            <div className="font-medium text-[#292723] mt-1 truncate">{ev.title}</div>
            <div className="text-xs text-[#5C5850] mt-0.5">{timeFmt.format(new Date(ev.when))}</div>
            {ev.attendees && <div className="text-xs text-[#5C5850] mt-1 truncate">👥 {ev.attendees}</div>}
            <div className="mt-1 flex flex-wrap gap-3 text-[11px] text-[#5C5850]">
              {ev.facility_id && facMap[ev.facility_id] && (
                <Link onClick={(e) => e.stopPropagation()} to={`/facilities/${ev.facility_id}`} className="hover:underline">🏢 {facMap[ev.facility_id]}</Link>
              )}
              {ev.np_id && npMap[ev.np_id] && (
                <Link onClick={(e) => e.stopPropagation()} to={`/nps/${ev.np_id}`} className="hover:underline">👩‍⚕️ {npMap[ev.np_id]}</Link>
              )}
            </div>
            {ev.notes && <div className="text-xs text-[#5C5850] mt-1.5 whitespace-pre-wrap line-clamp-3">{ev.notes}</div>}
          </div>
          {isEditable && (
            <button
              onClick={(e) => { e.stopPropagation(); remove(ev); }}
              className="text-[#C17D6E] hover:text-[#8A3E30] p-1 flex-shrink-0"
              data-testid={`event-remove-${ev.ref_id}`}
              aria-label="Delete"
              type="button"
            >
              <Trash2 className="h-3.5 w-3.5"/>
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-[#8A847A]">Workflow</div>
          <h1 className="heading text-3xl sm:text-4xl font-bold text-[#292723] mt-2">Calendar</h1>
          <p className="text-sm text-[#5C5850] mt-1.5">Meetings, follow-ups, and issue due dates in one place.</p>
        </div>
        <Button onClick={() => openNew()} className="bg-[#4A6B5D] hover:bg-[#3E594D]" data-testid="add-meeting-btn">
          <Plus className="h-4 w-4 mr-2"/> Schedule meeting
        </Button>
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        {/* Month view */}
        <div className="lg:col-span-2 bg-white border border-[#E5E0D8] rounded-md p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={prevMonth} data-testid="cal-prev-month" aria-label="Previous month">
                <ChevronLeft className="h-4 w-4"/>
              </Button>
              <div className="heading text-lg font-semibold min-w-[190px] text-center" data-testid="cal-month-label">
                {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
              </div>
              <Button variant="ghost" size="sm" onClick={nextMonth} data-testid="cal-next-month" aria-label="Next month">
                <ChevronRight className="h-4 w-4"/>
              </Button>
            </div>
            <Button variant="outline" size="sm" onClick={goToday} className="border-[#E5E0D8]" data-testid="cal-today-btn">Today</Button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-[0.06em] text-[#8A847A] pb-2">
            {DAY_LABELS.map((d) => <div key={d} className="px-1 text-center font-semibold">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((d, i) => {
              const inMonth = d.getMonth() === cursor.getMonth();
              const isToday = sameDay(d, today);
              const isSelected = sameDay(d, selected);
              const dayEvents = evsFor(d);
              return (
                <button
                  key={d.toISOString().slice(0, 10)}
                  onClick={() => setSelected(new Date(d))}
                  onDoubleClick={() => openNew(d)}
                  data-testid={`cal-day-${d.getFullYear()}-${d.getMonth()+1}-${d.getDate()}`}
                  className={`min-h-[86px] border rounded-md p-1.5 flex flex-col text-left transition-colors ${
                    isSelected ? "border-[#4A6B5D] ring-1 ring-[#4A6B5D]" : "border-[#E5E0D8] hover:border-[#C7C0B4]"
                  } ${inMonth ? "bg-white" : "bg-[#FAF8F3] text-[#B0AAA0]"}`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-medium ${isToday ? "bg-[#4A6B5D] text-white rounded-full h-5 w-5 grid place-items-center" : ""}`}>
                      {d.getDate()}
                    </span>
                    {dayEvents.length > 0 && (
                      <span className="text-[10px] text-[#8A847A]">{dayEvents.length}</span>
                    )}
                  </div>
                  <div className="mt-1 space-y-0.5 overflow-hidden">
                    {dayEvents.slice(0, 3).map((ev) => <EventChip key={ev.id} ev={ev} compact />)}
                    {dayEvents.length > 3 && (
                      <div className="text-[10px] text-[#8A847A] pl-1">+{dayEvents.length - 3} more</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-4 flex flex-wrap gap-3 pt-3 border-t border-[#E5E0D8] text-[11px] text-[#5C5850]">
            {Object.entries(KIND_STYLE).map(([k, s]) => (
              <div key={k} className="flex items-center gap-1.5">
                <span className="inline-block h-2 w-2 rounded-full" style={{ background: s.dot }} />
                <span>{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Day + upcoming panel */}
        <div className="space-y-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Selected day</div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openNew(selected)}
                className="border-[#E5E0D8] text-[#4A6B5D] hover:bg-[#F0EBE1] h-7 px-2"
                data-testid="cal-day-add-btn"
              >
                <Plus className="h-3.5 w-3.5 mr-1"/> Add
              </Button>
            </div>
            <div className="heading text-lg font-semibold" data-testid="cal-selected-label">{dayFmt.format(selected)}</div>
            <div className="mt-3 space-y-2" data-testid="cal-day-events">
              {selectedEvents.length === 0 ? (
                <button
                  onClick={() => openNew(selected)}
                  className="w-full bg-white border border-dashed border-[#E5E0D8] rounded-md p-6 text-center text-sm text-[#8A847A] hover:border-[#4A6B5D] hover:text-[#4A6B5D] transition-colors"
                  data-testid="cal-empty-day-add"
                >
                  <Plus className="h-4 w-4 inline mr-1"/> Nothing yet — tap to add an event or reminder
                </button>
              ) : selectedEvents.map((ev) => <EventChip key={ev.id} ev={ev} />)}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-[0.06em] text-[#5C5850] flex items-center gap-1.5">
                <Repeat className="h-3.5 w-3.5"/> Recurring series
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const seed = new Date(); seed.setHours(9, 0, 0, 0); seed.setDate(seed.getDate() + 1);
                  setEditing(null);
                  setForm({ title: "", when: toDatetimeLocal(seed), attendees: "", facility_id: "", np_id: "", notes: "", recurrence: "weekly", recurrence_until: "" });
                  setOpen(true);
                }}
                className="border-[#E5E0D8] text-[#4A6B5D] hover:bg-[#F0EBE1] h-7 px-2"
                data-testid="cal-new-recurring-btn"
              >
                <Plus className="h-3.5 w-3.5 mr-1"/> New recurring
              </Button>
            </div>
            {recurringSeries.length === 0 ? (
              <div className="bg-white border border-dashed border-[#E5E0D8] rounded-md p-4 text-center text-xs text-[#8A847A]">
                No recurring meetings yet. Tap "New recurring" to set up weekly rounds or monthly reviews.
              </div>
            ) : (
              <div className="space-y-2" data-testid="cal-recurring-list">
                {recurringSeries.map((s) => (
                  <div key={s.series_id} className="bg-white border border-[#E5E0D8] rounded-md p-2.5" data-testid={`cal-series-${s.series_id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <button
                        onClick={() => {
                          if (!s.next) return;
                          const d = new Date(s.next.when);
                          setCursor(new Date(d.getFullYear(), d.getMonth(), 1));
                          setSelected(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
                        }}
                        className="text-left flex-1 min-w-0"
                      >
                        <div className="text-sm font-medium text-[#292723] truncate">{s.title}</div>
                        <div className="text-[11px] text-[#8A847A] mt-0.5">
                          {s.count} occurrence{s.count === 1 ? "" : "s"}
                          {s.next ? ` · next ${new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(s.next.when))}` : " · all in the past"}
                        </div>
                      </button>
                      {s.next && (
                        <button
                          onClick={() => deleteSeries(s.next)}
                          className="text-[#8A847A] hover:text-[#C17D6E] p-1 flex-shrink-0"
                          aria-label="Delete series"
                          data-testid={`cal-series-delete-${s.series_id}`}
                          type="button"
                        >
                          <Trash2 className="h-3.5 w-3.5"/>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.06em] text-[#5C5850] mb-2">Next 14 days</div>
            <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1" data-testid="cal-upcoming">
              {upcoming.length === 0 ? (
                <div className="text-sm text-[#8A847A]">Nothing scheduled.</div>
              ) : upcoming.map((ev) => (
                <button
                  key={ev.id}
                  onClick={() => {
                    const d = new Date(ev.when);
                    setCursor(new Date(d.getFullYear(), d.getMonth(), 1));
                    setSelected(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
                  }}
                  className="w-full text-left bg-white border border-[#E5E0D8] rounded-md p-2.5 hover:border-[#4A6B5D] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="inline-block h-2 w-2 rounded-full" style={{ background: (KIND_STYLE[ev.kind] || KIND_STYLE.meeting).dot }} />
                    <span className="text-xs text-[#8A847A]">
                      {new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(ev.when))}
                    </span>
                  </div>
                  <div className="text-sm font-medium text-[#292723] truncate mt-0.5">{ev.title}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
        <SheetContent className="w-full sm:max-w-lg bg-white overflow-y-auto">
          <SheetHeader><SheetTitle className="heading text-2xl">{editing ? "Edit meeting" : "Schedule meeting"}</SheetTitle></SheetHeader>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Title</Label>
              <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 h-10 bg-white border-[#E5E0D8]" data-testid="meeting-title-input"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Date & time</Label>
              <Input required type="datetime-local" value={form.when} onChange={(e) => setForm({ ...form, when: e.target.value })} className="mt-1.5 h-10 bg-white border-[#E5E0D8]" data-testid="meeting-when-input"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Attendees</Label>
              <Input value={form.attendees} onChange={(e) => setForm({ ...form, attendees: e.target.value })} className="mt-1.5 h-10 bg-white border-[#E5E0D8]" placeholder="e.g., Shloime, Dr. Chen"/>
            </div>
            <SelectField label="Facility" value={form.facility_id} onChange={(v) => setForm({ ...form, facility_id: v })} options={[{value:"",label:"— none —"}, ...facs.map((f) => ({value: f.id, label: f.name}))]} />
            <SelectField label="NP" value={form.np_id} onChange={(v) => setForm({ ...form, np_id: v })} options={[{value:"",label:"— none —"}, ...nps.map((n) => ({value: n.id, label: n.name}))]} />
            {!editing && (
              <div className="grid grid-cols-2 gap-3">
                <SelectField
                  label="Repeat"
                  value={form.recurrence}
                  onChange={(v) => setForm({ ...form, recurrence: v })}
                  options={[
                    { value: "none", label: "Doesn't repeat" },
                    { value: "daily", label: "Daily" },
                    { value: "weekly", label: "Weekly" },
                    { value: "biweekly", label: "Every 2 weeks" },
                    { value: "monthly", label: "Monthly" },
                  ]}
                />
                <div>
                  <Label className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Until (optional)</Label>
                  <Input
                    type="date"
                    disabled={form.recurrence === "none"}
                    value={form.recurrence_until}
                    onChange={(e) => setForm({ ...form, recurrence_until: e.target.value })}
                    className="mt-1.5 h-10 bg-white border-[#E5E0D8] disabled:opacity-50"
                    data-testid="meeting-until-input"
                  />
                </div>
              </div>
            )}
            {editing?.series_id && (
              <div className="text-[11px] text-[#8A847A] bg-[#FAF6EE] border border-[#E7DDC5] rounded-md p-2">
                This meeting is part of a recurring series. Edits apply to just this occurrence; deletes offer to remove the whole future series.
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="mt-1.5 bg-white border-[#E5E0D8]"/>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => { setOpen(false); setEditing(null); }}>Cancel</Button>
              <Button type="submit" className="bg-[#4A6B5D] hover:bg-[#3E594D]" data-testid="meeting-save-btn">{editing ? "Save changes" : "Schedule"}</Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}
